import prepareComponent from "@bluframe/grapple/prepareComponent"
import {
  ReviewsServiceName,
  SelectedReview
} from "@bluframe/mightychroma-mightytool-frontend-types"
import { useCallback, useEffect, useState } from "react"

import PodSource from "./PodSource"

export interface Props {
  areReviewsLoading: boolean
  isCompact: boolean
  isCreated: boolean
  isLoading: boolean
  isValid: boolean
  label: string

  onClear: (serviceName: ReviewsServiceName) => () => void
  onSetReview: (
    serviceName: ReviewsServiceName
  ) => (review: SelectedReview["review"]) => void
  review?: SelectedReview["review"]
  serviceName: ReviewsServiceName
  text: string
}

export interface ComponentProps extends Omit<Props, "onClear" | "onSetReview"> {
  onCancel: () => void
  onClear: () => void
  onSave: () => void

  onSetReleaseDate: (releaseDate: string) => void
  onUpdateAudio: (update: ReviewSectionUpdate) => void
  onUpdateExtra: (update: ReviewSectionUpdate) => void
  onUpdateMovie: (update: ReviewSectionUpdate) => void
  onUpdateTotal: (update: ReviewSectionUpdate) => void
  onUpdateVideo: (update: ReviewSectionUpdate) => void
  review: SelectedReview["review"]
}

export type ReviewSectionUpdate = {
  quote?: string
  score?: number
  section?: string
}

const DEFAULT_REVIEW: SelectedReview["review"] = {}

// eslint-disable-next-line max-statements
const usePrepareComponent = ({
  onClear: onClearProps,
  onSetReview,
  serviceName,
  ...props
}: Props): ComponentProps => {
  const [newReview, setNewReview] = useState<SelectedReview["review"]>(
    () => props.review ?? DEFAULT_REVIEW
  )

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setNewReview(props.review ?? DEFAULT_REVIEW)
  }, [props.review])

  const onSetReleaseDate = (releaseDate: string) => {
    setNewReview((prev) => ({ ...prev, releaseDate }))
  }

  const onCancel = () => {
    setNewReview(props.review ?? DEFAULT_REVIEW)
  }

  const onUpdateAudio: ComponentProps["onUpdateAudio"] = useCallback(
    (update) => {
      setNewReview((prev) => ({
        ...prev,
        audio: { ...(prev.audio ?? {}), ...update }
      }))
    },
    []
  )

  const onUpdateExtra: ComponentProps["onUpdateExtra"] = useCallback(
    (update) => {
      setNewReview((prev) => ({
        ...prev,
        extra: { ...(prev.extra ?? {}), ...update }
      }))
    },
    []
  )

  const onUpdateMovie: ComponentProps["onUpdateMovie"] = useCallback(
    (update) => {
      setNewReview((prev) => ({
        ...prev,
        movie: { ...(prev.movie ?? {}), ...update }
      }))
    },
    []
  )

  const onUpdateTotal: ComponentProps["onUpdateTotal"] = useCallback(
    (update) => {
      setNewReview((prev) => ({
        ...prev,
        total: { ...(prev.total ?? {}), ...update }
      }))
    },
    []
  )

  const onUpdateVideo: ComponentProps["onUpdateVideo"] = useCallback(
    (update) => {
      setNewReview((prev) => ({
        ...prev,
        video: { ...(prev.video ?? {}), ...update }
      }))
    },
    []
  )

  const onClear = useCallback(() => {
    onClearProps(serviceName)()
  }, [onClearProps, serviceName])

  return {
    ...props,
    onCancel,
    onClear,
    onSave: () => onSetReview(serviceName)(newReview),
    onSetReleaseDate,
    onUpdateAudio,
    onUpdateExtra,
    onUpdateMovie,
    onUpdateTotal,
    onUpdateVideo,
    review: newReview,
    serviceName
  }
}

export default prepareComponent(usePrepareComponent)(PodSource)
